/**
 * FAQ — 3 variants (accordion, two_col, categorized)
 */
import { useState } from 'react';
import type { FaqContent } from '../schemas/faq';
import { Body, Eyebrow, H2, H3, SectionFrame } from './primitives';

interface FQ {
  content: FaqContent;
}

interface AccordionItemProps {
  question: string;
  answer: string;
  defaultOpen?: boolean;
}

function AccordionItem({ question, answer, defaultOpen = false }: AccordionItemProps) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div style={{ borderBottom: '1px solid var(--ls-border)' }}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        style={{
          width: '100%',
          padding: '20px 0',
          background: 'transparent',
          border: 'none',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 16,
          cursor: 'pointer',
          textAlign: 'left',
          color: 'var(--ls-text)',
          fontFamily: 'var(--ls-font-body)',
          fontSize: '1rem',
          fontWeight: 500,
        }}
        aria-expanded={open}
      >
        <span>{question}</span>
        <span
          style={{
            display: 'inline-block',
            transition: 'transform 0.2s',
            transform: open ? 'rotate(45deg)' : 'rotate(0deg)',
            color: 'var(--ls-text-muted)',
            fontSize: '1.25rem',
            lineHeight: 1,
          }}
        >
          +
        </span>
      </button>
      {open && (
        <div style={{ paddingBottom: 20 }}>
          <Body style={{ fontSize: '0.95rem' }}>{answer}</Body>
        </div>
      )}
    </div>
  );
}

export function FaqAccordion({ content }: FQ) {
  return (
    <SectionFrame>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 48 }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, maxWidth: 720, margin: '0 auto', alignItems: 'center', textAlign: 'center' }}>
          {content.eyebrow && <Eyebrow>{content.eyebrow}</Eyebrow>}
          <H2>{content.headline}</H2>
          {content.subheadline && <Body>{content.subheadline}</Body>}
        </div>
        <div style={{ maxWidth: 720, margin: '0 auto', width: '100%' }}>
          {content.items.map((item, i) => (
            <AccordionItem
              key={i}
              question={item.question}
              answer={item.answer}
              defaultOpen={i === 0}
            />
          ))}
        </div>
      </div>
    </SectionFrame>
  );
}

export function FaqTwoCol({ content }: FQ) {
  return (
    <SectionFrame background="surface">
      <div style={{ display: 'flex', flexDirection: 'column', gap: 48 }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, maxWidth: 800 }}>
          {content.eyebrow && <Eyebrow>{content.eyebrow}</Eyebrow>}
          <H2>{content.headline}</H2>
        </div>
        <div className="ls-grid ls-grid-2">
          {content.items.map((item, i) => (
            <div key={i} style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <H3 style={{ fontSize: '1.0625rem' }}>{item.question}</H3>
              <Body style={{ fontSize: '0.95rem' }}>{item.answer}</Body>
            </div>
          ))}
        </div>
      </div>
    </SectionFrame>
  );
}

export function FaqCategorized({ content }: FQ) {
  // Group by category
  const groups: Record<string, typeof content.items> = {};
  for (const item of content.items) {
    const key = item.category ?? '一般';
    if (!groups[key]) groups[key] = [];
    groups[key].push(item);
  }
  return (
    <SectionFrame>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 48 }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, maxWidth: 800 }}>
          {content.eyebrow && <Eyebrow>{content.eyebrow}</Eyebrow>}
          <H2>{content.headline}</H2>
        </div>
        <div className="ls-split-pair" style={{ gridTemplateColumns: '1fr 2fr', alignItems: 'stretch' }}>
          <div style={{ position: 'sticky', top: 80, alignSelf: 'flex-start', display: 'flex', flexDirection: 'column', gap: 8 }}>
            {Object.keys(groups).map((cat) => (
              <a
                key={cat}
                href={`#faq-${cat}`}
                style={{
                  fontSize: '0.95rem',
                  color: 'var(--ls-text-muted)',
                  textDecoration: 'none',
                  padding: '4px 0',
                }}
              >
                {cat}
              </a>
            ))}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 32 }}>
            {Object.entries(groups).map(([cat, items]) => (
              <div key={cat} id={`faq-${cat}`} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                <H3 style={{ fontSize: '1.0625rem' }}>{cat}</H3>
                <div>
                  {items.map((item, i) => (
                    <AccordionItem
                      key={i}
                      question={item.question}
                      answer={item.answer}
                    />
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </SectionFrame>
  );
}

export const faqVariants = {
  accordion: FaqAccordion,
  two_col: FaqTwoCol,
  categorized: FaqCategorized,
};
